import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toastify } from "../../components/Toast";
import "react-toastify/dist/ReactToastify.css";
import CreateVideoModal from "../AiModule/CreateVideoModal";
import { UrlConverter } from "../../utils/UrlConverter";

const industryIcons = {
  Technology: (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  ),
  "Food & Beverage": (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 21h8M12 17v4m-6-8a6 6 0 1112 0c0 3.314-2.686 6-6 6s-6-2.686-6-6z"
      />
    </svg>
  ),
  "Health & Fitness": (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 21C7.029 21 2.5 16.471 2.5 11.5S7.029 2 12 2s9.5 4.529 9.5 9.5S16.971 21 12 21z"
      />
    </svg>
  ),
  "Fashion & Retail": (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3z"
      />
    </svg>
  ),
  Gaming: (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 9V7a5 5 0 00-10 0v2a5 5 0 00-2 4v5a2 2 0 002 2h10a2 2 0 002-2v-5a5 5 0 00-2-4z"
      />
    </svg>
  ),
  Other: (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 7v4a1 1 0 001 1h3v2a1 1 0 001 1h4a1 1 0 001-1v-2h3a1 1 0 001-1V7"
      />
    </svg>
  ),
};

const defaultBrand = {
  name: "TechFlow",
  description: "AI-powered productivity software for teams",
  industry: "Technology",
  colors: ["#FFFFFF"],
  tone: "Professional, innovative",
  website: "https://techflow.com",
  targetMarket:
    "Small to medium businesses looking to improve team productivity",
  uniqueValue: "AI-powered automation that learns from your team's workflow",
  mainJob:
    "Facilitates remote project management.\nEnables real-time communication with team members.\nIntegrates various tools for seamless workflow.",
  differentiation:
    "Mobile-first design for ease of access on the go.\nRobust integration with widely-used digital tools.\nUser-friendly interface specifically tailored for remote teams.",
  howItHelps:
    "Provides a central hub for project updates and communication, making it easier to stay on track with work responsibilities, regardless of location.",
  features:
    "Comprehensive project management dashboard.\nReal-time chat and video conferencing capabilities.\nIntegration with popular cloud storage solutions.",
  benefits:
    "Enhances productivity while traveling.\nBridges communication gaps in remote teams.\nIncreases accountability and timelines for projects.",
};

const BrandDetail = ({
  brand: propBrand,
  onBrandUpdate,
  onGenerateContent,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [brand, setBrand] = useState(propBrand || defaultBrand);
  const [editedBrand, setEditedBrand] = useState(brand);
  const [, setLoading] = useState(true);
  const [loadError, setError] = useState("");
  const brandId = useParams();
  const navigate = useNavigate();
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  useState("ads");

  const Icon = industryIcons[brand.industry] || industryIcons.Other;

  useEffect(() => {
    // Refetch for this route/refresh only; discard the previous request on cleanup.
    const controller = new AbortController();
    let active = true;

    const token = localStorage.getItem("token");
    const fetchBrand = async () => {
      setLoading(true);
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}brands/${brandId.id}`,
          { signal: controller.signal,
            headers: {
              Authorization: `Bearer ${token}`, // Include the token in the request header
            },
          }
        );
        if (!active) return;
        if (!response?.data?.data || typeof response.data.data !== "object" || Array.isArray(response.data.data)) throw new Error("Missing detail data");
        setIsLoading(false);
        // setBrands(response.data.data);

        const Brand = {
          name: response.data.data.brandName,
          internalName: response.data.data.internalName,
          description: response.data.data.description,
          industry: response.data.data.industry,
          colors: Array.isArray(response.data.data.brandColors) ? response.data.data.brandColors : [],
          tone: response.data.data.brandToneAndVoice,
          website: response.data.data.url,
          targetMarket: response.data.data.targetMarket,
          // uniqueValue:
          //   "AI-powered automation that learns from your team's workflow",
          mainJob: response.data.data.mainJobOfProduct,
          differentiation: response.data.data.differentiation,
          howItHelps: response.data.data.howItHelps,
          features: response.data.data.features,
          benefits: response.data.data.benefits,
        };
        setBrand(Brand);
        setEditedBrand(Brand);
      } catch (err) {
        if (!active) return;
        setError("Failed to load brands. Please try again.");
        // setIsLoading(false);
      } finally {
        if (active) {
        // setIsLoading(false);
        setLoading(false);
        setIsLoading(false);

        }
}
    };
    fetchBrand();

    return () => { active = false; controller.abort(); };
  }, [brandId.id]);

  const handleSave = async (content = "") => {
    setApiError("");
    setIsSaving(true);
    const token = localStorage.getItem("token");

    // Map editedBrand to API payload
    const payload = {
      url: UrlConverter(editedBrand.website),
      brandName: editedBrand.name,
      internalName: editedBrand.internalName,
      industry: editedBrand.industry,
      description: editedBrand.description,
      brandColor: editedBrand.colors[0] || "",
      brandColors: editedBrand.colors,
      logo: {
        src: "", // Add logo upload logic if needed
        alt: "main logo",
        width: 0,
        height: 0,
      },
      logos: [], // Add multiple logo upload logic if needed
      mainJobOfProduct: editedBrand.mainJob,
      howItHelps: editedBrand.howItHelps,
      differentiation: editedBrand.differentiation,
      benefits: editedBrand.benefits,
      features: Array.isArray(editedBrand.features)
        ? editedBrand.features.map((f) => f.trim()).filter(Boolean)
        : (editedBrand.features || "")
            .split("\n")
            .map((f) => f.trim())
            .filter(Boolean),
      targetMarket: editedBrand.targetMarket,
      brandToneAndVoice: editedBrand.tone,
    };
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API}brands/${brandId.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setBrand(editedBrand);
      setIsEditing(false);
      setApiError("");
      toastify("success", response.data.message);
      setIsSaving(false);
      if (content === "generateContent") {
        navigate("/app/ads", {
          state: {
            openVideoModal: true,
            type: "image-ads",
            step: "content-type",
            brandId: brandId.id,
          },
        });
      }
    } catch (err) {
      setApiError("Failed to update brand. Please try again.");
      setIsSaving(false);
      toastify("error", "Failed to update brand.");
      console.error(err);
    }
  };

  const handleCancel = () => {
    setEditedBrand(brand);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditedBrand((prev) => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (index, color) => {
    const newColors = [...editedBrand.colors];
    newColors[index] = color;
    setEditedBrand((prev) => ({ ...prev, colors: newColors }));
  };

  const addColor = () => {
    const newColors = [...editedBrand.colors, "#3B82F6"];
    setEditedBrand((prev) => ({ ...prev, colors: newColors }));
  };

  const removeColor = (index) => {
    // if (editedBrand.colors.length > 1) {
    const newColors = editedBrand.colors.filter((_, i) => i !== index);
    setEditedBrand((prev) => ({ ...prev, colors: newColors }));
    // }
  };

  const handleAIAnalysis = async () => {
    const token = localStorage.getItem("token");
    if (!editedBrand.website) {
      alert("Please enter a website URL first");
      return;
    }
    const payload = {
      brandId: brandId.id,
      websiteUrl: editedBrand.website,
    };
    setIsAnalyzing(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}brands/update-brand-from-website`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const Brand = {
        name: response.data.data.brandName,
        description: response.data.data.description,
        industry: response.data.data.industry,
        colors: Array.isArray(response.data.data.brandColors) ? response.data.data.brandColors : [],
        tone: response.data.data.brandToneAndVoice,
        website: response.data.data.url,
        targetMarket: response.data.data.targetMarket,
        // uniqueValue: response.data.data.uniqueValue,
        mainJob: response.data.data.mainJobOfProduct,
        differentiation: response.data.data.differentiation,
        howItHelps: response.data.data.howItHelps,
        features: response.data.data.features,
        benefits: response.data.data.benefits,
      };
      setBrand(Brand);
      setEditedBrand(Brand);
      setIsAnalyzing(false);
      // setIsEditing(false);
      setApiError("");
      toastify("success", response.data.message);
    } catch (err) {
      setApiError("Failed to update brand. Please try again.");
      toastify("error", err.response.data.message);
      console.error(err);
    }
  };

  const handleDeleteBrand = async () => {
    if (window.confirm("Are you sure you want to delete this brand?")) {
      const token = localStorage.getItem("token");
      // navigate("/app/audiences");
      try {
        const response = await axios.delete(
          `${process.env.REACT_APP_API}brands/${brandId.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toastify("success", response.data.message);
        setTimeout(() => {
          navigate("/app/brands");
        }, 1000);
      } catch (error) {
        console.error(error);
        toastify("error", error.response.data.message);
      }
    }
  };

  if (loadError) return <div role="alert">{loadError}</div>;
  return (
    <>
      {isLoading ? (
        <div className="App-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      ) : (
        <div className="responsive-page responsive-page--brand-detail max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                {Icon}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-[#fff]">
                  {brand?.internalName || brand?.name}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 truncate lg:w-[400px] overflow-hidden whitespace-nowrap md:w-[400px] sm:w-[200px] w-[150px]">
                  {brand.description}
                </p>
                <h3 className="text-md text-gray-500 dark:text-gray-400">
                  Brand Name: {brand.name}
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditing && (
                <button
                  onClick={handleAIAnalysis}
                  disabled={isAnalyzing || !editedBrand.website}
                  className="border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-4 py-2 rounded flex items-center"
                >
                  {isAnalyzing ? (
                    <span className="animate-spin inline-block mr-2">⏳</span>
                  ) : (
                    <span className="mr-2">🧠</span>
                  )}
                  AI Website Analysis
                </button>
              )}
              {!isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded mr-2"
                  >
                    Edit Brand
                  </button>
                  <button
                    onClick={handleDeleteBrand}
                    className="border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                  <button
                    onClick={() => setShowGenerateModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
                  >
                    Generate Content
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-4 py-2 rounded mr-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
                  >
                    Save Changes
                  </button>
                </>
              )}
            </div>
          </div>

          {apiError && (
            <div className="bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-4 py-2 rounded mb-4">
              {apiError}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="lg:col-span-2 bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6 overflow-hidden">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Basic Information
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Project Name
                  </label>
                  {isEditing ? (
                    <>
                      <input
                        value={editedBrand.internalName}
                        onChange={(e) =>
                          handleInputChange("internalName", e.target.value)
                        }
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        This name helps you identify different versions of the
                        same brand. (e.g. "Acme Holiday Launch" or "Acme Black
                        Friday Sale"). Only visible to you.
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-900 dark:text-[#fff] font-medium">
                      {brand?.internalName}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Brand Name
                    </label>
                    {isEditing ? (
                      <>
                        <input
                          value={editedBrand.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          The official brand name used in all generated content.
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-900 dark:text-[#fff] font-medium">
                        {brand.name}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Industry
                    </label>
                    {isEditing ? (
                      <input
                        value={editedBrand.industry}
                        onChange={(e) =>
                          handleInputChange("industry", e.target.value)
                        }
                        className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      />
                    ) : (
                      <span className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm text-gray-700 dark:text-gray-300">
                        {brand.industry}
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  {isEditing ? (
                    <textarea
                      value={editedBrand.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[80px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-[#fff]">
                      {brand.description}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Website
                  </label>
                  {isEditing ? (
                    <input
                      value={editedBrand.website}
                      onChange={(e) =>
                        handleInputChange("website", e.target.value)
                      }
                      className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                      placeholder="https://yourbrand.com"
                    />
                  ) : (
                    <a
                      href={brand.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-words text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
                    >
                      {brand.website}
                    </a>
                  )}
                </div>
              </div>
            </div>
            {/* Brand Colors */}
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff]">
                  Brand Colors
                </h3>
                {isEditing && (
                  <button
                    type="button"
                    onClick={addColor}
                    className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 py-1 rounded text-sm"
                  >
                    Add
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {(isEditing ? editedBrand.colors : brand.colors).map(
                  (color, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-gray-700 dark:text-gray-300">
                          {index === 0 && "Primary Color"}
                          {index === 1 && "Secondary Color"}
                          {index > 1 && `Color ${index + 1}`}
                        </label>

                        {isEditing && editedBrand.colors.length && (
                          <button
                            type="button"
                            onClick={() => removeColor(index)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={editedBrand.colors[index]}
                            onChange={(e) =>
                              handleColorChange(index, e.target.value)
                            }
                            className="w-12 h-10 rounded border border-gray-300 dark:border-gray-600"
                          />
                          <input
                            value={editedBrand.colors[index]}
                            onChange={(e) =>
                              handleColorChange(index, e.target.value)
                            }
                            className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-gray-700 dark:text-gray-300 font-mono text-sm">
                            {color}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Product Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Main Job of Your Product
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.mainJob}
                  onChange={(e) => handleInputChange("mainJob", e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="List the main tasks or problems your product helps users solve"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.mainJob || "Not specified"}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                How It Helps
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.howItHelps}
                  onChange={(e) =>
                    handleInputChange("howItHelps", e.target.value)
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="Explain how your product helps users achieve their goals"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.howItHelps || "Not specified"}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Differentiation
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.differentiation}
                  onChange={(e) =>
                    handleInputChange("differentiation", e.target.value)
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="Explain what makes your product unique compared to alternatives"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.differentiation || "Not specified"}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Benefits
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.benefits}
                  onChange={(e) =>
                    handleInputChange("benefits", e.target.value)
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="List the key benefits of your product"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.benefits || "Not specified"}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Features
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.features}
                  onChange={(e) =>
                    handleInputChange("features", e.target.value)
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="List the key features of your product"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.features || "Not specified"}
                </div>
              )}
            </div>
            <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
                Target Market
              </h3>
              {isEditing ? (
                <textarea
                  value={editedBrand.targetMarket}
                  onChange={(e) =>
                    handleInputChange("targetMarket", e.target.value)
                  }
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                  placeholder="Describe your primary target audience"
                />
              ) : (
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {brand.targetMarket || "Not specified"}
                </div>
              )}
            </div>
          </div>

          {/* Brand Voice */}
          <div className="bg-white dark:bg-[#0A0A0A] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-4">
              Brand Tone & Voice
            </h3>
            {isEditing ? (
              <textarea
                value={editedBrand.tone}
                onChange={(e) => handleInputChange("tone", e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-[#fff]"
                placeholder="e.g., Professional, friendly, innovative"
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300">{brand.tone}</p>
            )}
          </div>

          {/* Quick Actions */}
          {isEditing && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-[#fff] mb-2">
                  Ready to create content?
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Use this comprehensive brand profile to generate targeted
                  content for your audience.
                </p>
              </div>
              <button
                disabled={!isEditing}
                onClick={() => {
                  handleSave("generateContent");
                }}
                className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
              >
                <span className="text-xl">✨</span>
                {!isSaving ? "Save & Generate content" : "Saving..."}
              </button>
            </div>
          )}
          {/* <ToastContainer /> */}
          {/* Generate Content Modal */}
          <CreateVideoModal
            open={showGenerateModal}
            onOpenChange={setShowGenerateModal}
            selectedBrandId={brandId.id}
            selectedBrandName={brand.name}
          />
        </div>
      )}
    </>
  );
};

export default BrandDetail;
